import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Globe, MapPin, ArrowRight, Truck, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DestinationsPage() {
  const [destinations, setDestinations] = useState({ regions: [], routes: [] });
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language, localizedPath } = useSiteContent();
  
  // Set page title
  usePageTitle('destinations');

  // Default translations
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'ДЕСТИНАЦИИ',
    heroTitle: 'Свързваме',
    heroTitleHighlight: 'Европа',
    heroDescription: 'Обслужваме всички основни европейски дестинации с редовни маршрути и експресни опции.',
    routesSubtitle: 'ПОПУЛЯРНИ МАРШРУТИ',
    routesTitle: 'Основни',
    routesTitleHighlight: 'транспортни линии',
    coverageSubtitle: 'ПОКРИТИЕ',
    coverageTitle: 'Държави, които',
    coverageTitleHighlight: 'обслужваме',
    via: 'Чрез',
    ctaTitle: 'Дестинацията не е в списъка?',
    ctaText: 'Можем да организираме транспорт до почти всяка европейска локация.',
    ctaButton: 'Свържете се с нас'
  } : {
    heroSubtitle: 'DESTINATIONS',
    heroTitle: 'Connecting',
    heroTitleHighlight: 'Europe',
    heroDescription: 'We serve all major European destinations with regular routes and express options.',
    routesSubtitle: 'POPULAR ROUTES',
    routesTitle: 'Main',
    routesTitleHighlight: 'Transport Lines',
    coverageSubtitle: 'COVERAGE',
    coverageTitle: 'Countries We',
    coverageTitleHighlight: 'Serve',
    via: 'Via',
    ctaTitle: 'Destination Not Listed?',
    ctaText: 'We can arrange transport to virtually any European location.',
    ctaButton: 'Contact Us'
  };

  // Merge database content with defaults
  const content = {
    heroSubtitle: (language === 'bg' ? pageContent?.hero_subtitle_bg : pageContent?.hero_subtitle_en) || defaultContent.heroSubtitle,
    heroTitle: (language === 'bg' ? pageContent?.hero_title_bg : pageContent?.hero_title_en) || defaultContent.heroTitle,
    heroTitleHighlight: (language === 'bg' ? pageContent?.hero_title_highlight_bg : pageContent?.hero_title_highlight_en) || defaultContent.heroTitleHighlight,
    heroDescription: (language === 'bg' ? pageContent?.hero_description_bg : pageContent?.hero_description_en) || defaultContent.heroDescription,
    routesSubtitle: (language === 'bg' ? pageContent?.routes_subtitle_bg : pageContent?.routes_subtitle_en) || defaultContent.routesSubtitle,
    routesTitle: (language === 'bg' ? pageContent?.routes_title_bg : pageContent?.routes_title_en) || defaultContent.routesTitle,
    routesTitleHighlight: (language === 'bg' ? pageContent?.routes_title_highlight_bg : pageContent?.routes_title_highlight_en) || defaultContent.routesTitleHighlight,
    coverageSubtitle: (language === 'bg' ? pageContent?.coverage_subtitle_bg : pageContent?.coverage_subtitle_en) || defaultContent.coverageSubtitle,
    coverageTitle: (language === 'bg' ? pageContent?.coverage_title_bg : pageContent?.coverage_title_en) || defaultContent.coverageTitle,
    coverageTitleHighlight: (language === 'bg' ? pageContent?.coverage_title_highlight_bg : pageContent?.coverage_title_highlight_en) || defaultContent.coverageTitleHighlight,
    via: (language === 'bg' ? pageContent?.via_bg : pageContent?.via_en) || defaultContent.via,
    ctaTitle: (language === 'bg' ? pageContent?.cta_title_bg : pageContent?.cta_title_en) || defaultContent.ctaTitle,
    ctaText: (language === 'bg' ? pageContent?.cta_text_bg : pageContent?.cta_text_en) || defaultContent.ctaText,
    ctaButton: (language === 'bg' ? pageContent?.cta_button_bg : pageContent?.cta_button_en) || defaultContent.ctaButton
  };

  useEffect(() => {
    const fetchData = async () => { 
      try { 
        const [destRes, contentRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/destinations-page-content`)
        ]);
        setDestinations(destRes.data);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) { 
        console.error('Error:', error); 
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get localized value
  const getLocalized = (item, field) => {
    if (language === 'bg' && item[`${field}_bg`]) {
      return item[`${field}_bg`];
    }
    return item[field] || item[`${field}_en`] || '';
  };

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="destinations-page" className="pt-20 bg-slate-50">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* Main Routes */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="overline mb-4 block">{content.routesSubtitle}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              {content.routesTitle} <span className="text-[#C41E3A]">{content.routesTitleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(destinations.routes || []).map((route, index) => (
              <Card key={index} className="bg-white border-slate-100 hover:shadow-lg transition-all rounded-3xl animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Truck className="w-5 h-5 text-[#C41E3A]" />
                    <h3 className="font-bold text-slate-900">
                      {language === 'bg' && route.name_bg ? route.name_bg : (route.name_en || route.name)}
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Clock className="w-4 h-4" />
                      <span>{language === 'bg' && route.time_bg ? route.time_bg : (route.time_en || route.time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <CheckCircle className="w-4 h-4 text-[#C41E3A]" />
                      <span>{language === 'bg' && route.frequency_bg ? route.frequency_bg : (route.frequency_en || route.frequency)}</span>
                    </div>
                    {(route.via || route.via_en || route.via_bg) && (
                      <div className="text-slate-400 text-xs">
                        {content.via}: {language === 'bg' && route.via_bg ? route.via_bg : (route.via_en || route.via)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section data-testid="destinations-grid" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">{content.coverageSubtitle}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {content.coverageTitle} <span className="text-[#C41E3A]">{content.coverageTitleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinations.regions.map((region, index) => {
              const regionName = language === 'bg' && region.name_bg ? region.name_bg : (region.name_en || region.name);
              const countries = language === 'bg' && region.countries_bg ? region.countries_bg : (region.countries_en || region.countries);
              return (
                <Card key={index} data-testid={`region-card-${(region.name || '').toLowerCase().replace(/\s+/g, '-')}`} className="bg-slate-50 border-slate-100 rounded-3xl animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-3xl">{region.emoji || '🌍'}</span>
                      <h3 className="text-xl font-bold text-slate-900">{regionName}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(countries || []).map((country, i) => (
                        <span key={i} className="destination-badge text-sm bg-white">
                          <MapPin className="w-3 h-3 text-[#C41E3A]" />{country}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <Link to={localizedPath('/contact')}>
            <button data-testid="destinations-contact-btn" className="btn-modern btn-light btn-lg">
              {content.ctaButton} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
